/** @jsxImportSource @emotion/react */
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { Table } from "../../../components/table-components/Table";
import { TableBody } from "../../../components/table-components/TableBody";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { TableRow } from "../../../components/table-components/TableRow";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";
import { firstTableCellStyle } from "../../../style-util";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";
import { PaymentProviderConfiguration } from "../models/payment-provider-configuration";
import { MutatePaymentProviderConfigurationDrawer } from "./MutatePaymentProviderConfigurationDrawer";
import { useDeletePaymentProviderConfiguration } from "../service/payment-provider-configrations-api";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../module/auth/authSelectors";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";

interface Props {
  paymentProviderConfigurations: PaymentProviderConfiguration[];
}

export const PaymentProvidersConfigurationsTable = ({
  paymentProviderConfigurations,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { option, setOption, removeOption } = useSearchParamsManipulation();
  const {
    mutateAsync: deletePaymentProviderConfiguration,
    isLoading,
  } = useDeletePaymentProviderConfiguration();

  return (
    <>
      <Table>
        <thead>
          <TableRowHeader>
            <TableCellHeaderText css={firstTableCellStyle}>
              Provider name
            </TableCellHeaderText>
            <TableCellHeaderText>Country</TableCellHeaderText>
            <TableCellHeaderText>Enabled</TableCellHeaderText>
            <TableCellHeaderText>For testing?</TableCellHeaderText>
            <th />
          </TableRowHeader>
        </thead>
        <TableBody>
          {paymentProviderConfigurations.map(
            (paymentProviderConfiguration, i) => (
              <TableRow key={i}>
                <TableCellText
                  css={firstTableCellStyle}
                  onClick={() => {
                    setOption(paymentProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {paymentProviderConfiguration.providerName || "-"}
                  </StyledText>
                </TableCellText>
                <TableCellText
                  css={firstTableCellStyle}
                  onClick={() => {
                    setOption(paymentProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {paymentProviderConfiguration.countryCode || "-"}
                  </StyledText>
                </TableCellText>
                <TableCellText
                  onClick={() => {
                    setOption(paymentProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {paymentProviderConfiguration.enabled ? "Yes" : "No"}
                  </StyledText>
                </TableCellText>
                <TableCellText
                  onClick={() => {
                    setOption(paymentProviderConfiguration.id);
                  }}
                >
                  <StyledText
                    className="bodyTextRegular mb-0"
                    color={colors.textPrimary}
                  >
                    {paymentProviderConfiguration.testing ? "Yes" : "No"}
                  </StyledText>
                </TableCellText>
                <TableEllipsisDropdown
                  menu={{
                    items: [
                      {
                        key: "1",
                        label: (
                          <DropdownItemButton
                            onClick={() => {
                              setOption(paymentProviderConfiguration.id);
                            }}
                            type="button"
                            icon={<FontAwesomeIcon icon={solid("pen")} />}
                          >
                            Edit
                          </DropdownItemButton>
                        ),
                      },
                      {
                        key: "2",
                        label: (
                          <DropdownItemButton
                            disabled={isLoading}
                            onClick={async () => {
                              try {
                                await deletePaymentProviderConfiguration({
                                  organizationKeyName,
                                  data: { id: paymentProviderConfiguration.id },
                                });
                                toastr.success(
                                  "Success",
                                  "Payment provider configuration deleted successfully"
                                );
                              } catch (error) {
                                const errorMessage = getErrorMessage(error);
                                toastr.error("Error", errorMessage);
                              }
                            }}
                            type="button"
                            icon={<FontAwesomeIcon icon={solid("trash")} />}
                          >
                            Delete
                          </DropdownItemButton>
                        ),
                      },
                    ],
                  }}
                />
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
      <MutatePaymentProviderConfigurationDrawer
        isOpen={!!option}
        existingPaymentProviders={paymentProviderConfigurations.map(
          (paymentProviderConfiguration) =>
            paymentProviderConfiguration.provider
        )}
        onClose={() => {
          removeOption();
        }}
      />
    </>
  );
};
